import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  Box,
  Collapse,
  IconButton,
  Link,
  ListItem,
  ListItemIcon,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/system/Grid";
import { debounce } from "lodash";

import {
  CollapsibleNavbar,
  FaAnglesLeftIcon,
  FaAnglesRightIcon,
  FaCircleIcon,
  IconButtonStyle,
  IconGrid,
  IconStyle,
  LeftNavBackground,
  MenuOuterBoxStyle,
  MenuTextColorChange,
  SubMenuItem,
  SubMenuLink,
} from "../styles/styles";
import MainContent from "../../components/MainContent";
import { ApplicationTitles, FeatureId, LeftMenuOption, LeftMenuOptionType, MenuItemType, ResetAuthReAuthTypes, SubMenuType } from "../../constants/HomePage";
import fontWeight from "../../styles/mui/fontWeight";
import { getDownloadUrl, getSessionStorage, getValue, hasAccess } from "../../utils";
import { ServiceProvider } from "../../models/ServiceProvider";
import { MyAccountModel } from "../../models/MyAccount";
import { ApplicationRoute } from "../../constants/ApplicationUrl";
import { FeatureAccess } from "../../models/Master";
import { UserContext, UserContextType } from "./AppContent";
import { ReferralDetail } from "../../models/Referral";
import { MasterServiceProviderActionDispatchTypes, MasterServiceProviderDefaultActionDispatchTypes } from "../../constants/ServiceProvider";
import { DocumentDownloadActionDispatchTypes, Numbers } from "../../constants/Referral";
import { SsoHppClaimsStatusUrlActionDispatchTypes, SsoHpsClaimsStatusUrlActionDispatchTypes } from "../../constants/User";
import { SsoHppUrlResponse, SsoHpsUrlResponse } from "../../models/User";
import { showMessageModal } from "../common";
import { hpsClaimsStatusFormUrl, samlCallback } from "../../constants/Config";

export interface PropsFromState {
  masterServiceProviderList: ServiceProvider[];
  loggedInUser: MyAccountModel;
  masterFeatureAccessState: FeatureAccess[] | null;
  referralList: ReferralDetail[];
  referralSearchList: ReferralDetail[];
  resetStates: (type: string) => void;
  getMasterServiceProvider: () => void;
  getMasterServiceProviderDefault: () => void;
  getSsoHpsUrl: (payload: { userId: string, npiId: number }) => void;
  getSsoHppUrl: () => void;
  ssoHpsClaimsStatusUrlState: SsoHpsUrlResponse | null;
  ssoHppClaimsStatusUrlState: SsoHppUrlResponse | null;
  masterServiceProviderErrorList: any;
}

type AllProps = PropsFromState;

const LeftNavbar: React.FC<AllProps> = ({
  masterServiceProviderList,
  masterFeatureAccessState,
  referralList,
  referralSearchList,
  resetStates,
  getMasterServiceProvider,
  getMasterServiceProviderDefault,
  getSsoHpsUrl,
  getSsoHppUrl,
  loggedInUser,
  ssoHpsClaimsStatusUrlState,
  ssoHppClaimsStatusUrlState,
  masterServiceProviderErrorList,
}) => {
  const { roleId } = useContext<UserContextType>(UserContext);

  const [open, setOpen] = useState<boolean>(true);
  const [expandedMenu, setExpandedMenu] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();

  const handleToggle = () => {
    setOpen(!open);
  };
  const handleMenuItemClick = (menuItem: MenuItemType) => {
    ResetAuthReAuthTypes.forEach((actionType) => resetStates(actionType));
    if (menuItem.subMenu) {
      setExpandedMenu(!expandedMenu);
    } else if (ApplicationTitles.SSO_HPS === menuItem.title) { 
      const getNpiId = () => {
        const agencyStorageData = getSessionStorage("agencyStorage");
        const selectedServiceRequestId = getValue(agencyStorageData, "value");
        const selectedProvider =
          masterServiceProviderList &&
          masterServiceProviderList.filter(
            (item: ServiceProvider) =>
              Number(item.serviceProviderId) ===
              Number(selectedServiceRequestId),
          );
        return (
          (selectedProvider &&
          selectedProvider[0] !== undefined &&
          selectedProvider[0].providerNpi) ?? 0
        );
      };
      getSsoHpsUrl({userId: getValue(loggedInUser, "providerPortalUser.userId"), npiId: Number(getNpiId())});

    } else if (ApplicationTitles.HPP_SSO === menuItem.title) { 
      getSsoHppUrl();
    } else {
      setExpandedMenu(false);
      resetStates(MasterServiceProviderActionDispatchTypes.MASTER_SERVICE_PROVIDER_RESET)
      resetStates(DocumentDownloadActionDispatchTypes.DOCUMENT_DOWNLOAD_RESET)
      navigate(menuItem.path);
    }
  };

  const checkPriorAuthAccess = (masterServiceProviderList: ServiceProvider[]) => {
    const agencyStorageData = getSessionStorage("agencyStorage");
    const selectedServiceRequestId = getValue(agencyStorageData, "value")
    const selectedProvider = masterServiceProviderList && masterServiceProviderList.filter((item: ServiceProvider) => Number(item.serviceProviderId) === Number(selectedServiceRequestId))
    return selectedProvider && selectedProvider[0] !== undefined && selectedProvider[0].isPriorAuthEnable;
  }
  const [filteredMenuOptions, setFilteredMenuOptions] = useState<LeftMenuOptionType[]>();

  const [providerFetchAttempts, setProviderFetchAttempts] = useState<number>(Numbers.ZERO);
  const [errorModal, setErrorModal] = useState<boolean>(false);
  const [confirm, setConfirm] = useState<boolean>(false);

  useEffect(() => {
    resetStates(MasterServiceProviderActionDispatchTypes.MASTER_SERVICE_PROVIDER_RESET);
    resetStates(MasterServiceProviderDefaultActionDispatchTypes.MASTER_SERVICE_PROVIDER_DEFAULT_RESET);
    setProviderFetchAttempts(0);
   }, []);

   useEffect(() => {
    const debouncedFilter = debounce(() => {
      const roleType = loggedInUser?.providerPortalUser?.roleType;
      const healthPlan = loggedInUser?.providerPortalUser?.healthPlan;

      const isTangoAdmin = roleType === "Tango Admin";
      const hasHealthPlan = Boolean(healthPlan);

      const filteredMenu = LeftMenuOption.filter((option: LeftMenuOptionType) => {
        const canAccessFeature =
          hasAccess(roleId, masterFeatureAccessState, option.featureId) &&
          hasHealthPlan;
        if (option.path === ApplicationRoute.HPP_SSO) {
          return isTangoAdmin || canAccessFeature;
        }
        if (option.path === ApplicationRoute.MEMBER_INFO) {
          if (
            masterServiceProviderList &&
            masterServiceProviderList.length === Numbers.ZERO &&
            providerFetchAttempts < Numbers.Four
          ) {
            if (typeof getMasterServiceProvider === "function") {
              getMasterServiceProvider();
              getMasterServiceProviderDefault();
              setProviderFetchAttempts((prev) => prev + Numbers.ONE);
            }
          }

          return checkPriorAuthAccess(masterServiceProviderList);
        }

        return true;
      });

      setFilteredMenuOptions(filteredMenu);
    }, 300);

    debouncedFilter();

    return () => {
      debouncedFilter.cancel();
    };
  }, [
    referralSearchList,
    referralList,
    masterServiceProviderList,
    providerFetchAttempts,
    loggedInUser?.providerPortalUser?.roleType,
    loggedInUser?.providerPortalUser?.healthPlan,
  ]);

  useEffect(() => { 
    if (masterServiceProviderErrorList !== null && providerFetchAttempts === Numbers.Four && confirm === false && window.location.pathname === "/") {
    setErrorModal(true);
    }
  },[masterServiceProviderErrorList]);

  useEffect(() => {
    if (
      ssoHppClaimsStatusUrlState &&
      getValue(ssoHppClaimsStatusUrlState, "ssoUrl").length > 0
    ) {
      const samlResponse = getValue(ssoHppClaimsStatusUrlState, "samlResponse");
      if (!samlResponse) return;

      const form = document.createElement("form");
      form.method = "POST";
      form.action = samlCallback;
      form.target = "_blank";
      form.style.display = "none";

      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "SamlResponse";
      input.value = samlResponse;

      form.appendChild(input);
      document.body.appendChild(form);

      try {
        form.submit();
      } finally {
        document.body.removeChild(form);
      }
      setTimeout(() => {
        resetStates(
          SsoHppClaimsStatusUrlActionDispatchTypes.SSO_HPP_CLAIMS_STATUS_RESET,
        );
      }, 1000);
    }
  }, [ssoHppClaimsStatusUrlState]);

  useEffect(() => {
      if (
      ssoHpsClaimsStatusUrlState &&
      getValue(ssoHpsClaimsStatusUrlState, "ssoUrl").length > 0 && hpsClaimsStatusFormUrl !== ""
    ) {
      const samlResponse = getValue(ssoHpsClaimsStatusUrlState, "samlResponse");
      if (!samlResponse) return;

      const form = document.createElement("form");
      form.method = "POST";
      form.action = hpsClaimsStatusFormUrl;
      form.target = "_blank";
      form.style.display = "none";

      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "SamlResponse";
      input.value = samlResponse;

      form.appendChild(input);
      document.body.appendChild(form);

      try {
        form.submit();
      } finally {
        document.body.removeChild(form);
      }
      setTimeout(() => {
        resetStates(
          SsoHpsClaimsStatusUrlActionDispatchTypes.SSO_HPS_CLAIMS_STATUS_RESET,
        );
      }, 1000);
    }
  }, [ssoHpsClaimsStatusUrlState]);

  return (
    <>
      <Box sx={MenuOuterBoxStyle(open)}>
        <Grid size={{ xs: 12 }} sx={LeftNavBackground}>
          <Grid container>
            <Grid size={{ xs: 12 }} sx={IconGrid}>
              <IconButton onClick={handleToggle} sx={IconButtonStyle}>
                {open ? <FaAnglesLeftIcon /> : <FaAnglesRightIcon />}
              </IconButton>
            </Grid>
          </Grid>
          <Collapse in={open || !open} sx={CollapsibleNavbar}>
            {filteredMenuOptions && filteredMenuOptions.map((option: LeftMenuOptionType) => {
              const IconComponent: any = option.icon;
              const restrictedFeatureIds = [FeatureId.SCORECARD, FeatureId.SSO_HPS];
              const isRestrictedFeature = restrictedFeatureIds.includes(option.featureId);
              const canAccessFeature = hasAccess(roleId, masterFeatureAccessState, option.featureId);

              return (isRestrictedFeature && !canAccessFeature ? null :
                <React.Fragment key={option.path}>
                <Tooltip title={!open ? option.title : ""} arrow placement="right">
                  <MenuItem
                    onClick={() => handleMenuItemClick(option)}
                    sx={MenuTextColorChange(location.pathname, option.path)}
                  >
                    <IconComponent
                      style={IconStyle(open, location.pathname, option.path)}
                    />
                    {open && (
                      <Typography variant="h3" fontWeight={fontWeight[2]}>
                        {option.title}
                      </Typography>
                    )}
                  </MenuItem>
                </Tooltip>
                  {option.subMenu &&
                    expandedMenu &&
                    option.subMenu.map((subOption: SubMenuType) => (
                      <MenuItem key={subOption.title} sx={SubMenuItem}>
                        {open && (
                          <ListItem>
                            <ListItemIcon>
                              <FaCircleIcon />
                            </ListItemIcon>
                            <Typography>
                              <Link
                                href={getDownloadUrl(subOption.link)}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={SubMenuLink}
                              >
                                {subOption.title}
                              </Link>
                            </Typography>
                          </ListItem>
                        )}
                      </MenuItem>
                    ))}
                </React.Fragment>
              );
            })}
          </Collapse>
        </Grid>
      </Box>
      <Grid size={{ xs: 12 }}>
        <MainContent loggedInUser={loggedInUser} />
      </Grid>
      {(errorModal) &&
        showMessageModal(
          errorModal,
          setErrorModal,
          "Unable to fetch provider details. Please reload the page again after sometime.",
          setConfirm
        )}
    </>
  );
};
export default LeftNavbar;
